import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { fetchTickets, fetchTicket, createTicket, updateTicket, fetchAccountDetails } from '../api/ticketData';
import { useAuth0 } from '../components/auth/DevAuth0Provider';
import { useAuthUser } from './AuthUserContext';

const TicketContext = createContext();

export const TicketProvider = ({ children }) => {
    const [tickets, setTickets] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [adminAccountFilter, setAdminAccountFilter] = useState('all'); // New state for admin account filtering

    const [zohoAuth, setZohoAuth] = useState({ required: false, url: null });
    const { isAuthenticated } = useAuth0();
    const user = useAuthUser();
    const loadTicketsRef = useRef(null);

    // Helper function to filter tickets based on user organization
    const filterTicketsByOrganization = useCallback(async (allTickets) => {
        // If user is helios admin
        if (user?.role === 'helios_admin' || user?.roles?.includes('helios_admin')) {
            // If admin has selected a specific account filter, filter by that account
            if (adminAccountFilter !== 'all') {
                // console.log('TicketContext: Admin filtering by account:', adminAccountFilter);
                const filteredTickets = allTickets.filter(ticket => ticket.accountId === adminAccountFilter);
                // console.log(`TicketContext: Admin filtered ${filteredTickets.length} tickets out of ${allTickets.length} total tickets for account ${adminAccountFilter}`);
                return filteredTickets;
            } else {
                // console.log('TicketContext: User is helios_admin, showing all tickets');
                return allTickets;
            }
        }

        // console.log('TicketContext: User:', user);

        // If user doesn't have organization info, return empty array
        if (!user?.customerId && !user?.tenantId) {
            // console.log('TicketContext: User has no organization info, showing no tickets');
            return [];
        }

        const userOrganizationName = (user.customerId || user.tenantId || '').toLowerCase();
        // console.log('TicketContext: Filtering tickets for organization:', userOrganizationName);

        const filteredTickets = [];
        
        // Check each ticket's accountId against user organization
        for (const ticket of allTickets) {
            // console.log(`TicketContext: Processing ticket ${ticket.id}, accountId: ${ticket.accountId}`);
            
            if (!ticket.accountId) {
                // console.log(`TicketContext: Skipping ticket ${ticket.id} - no accountId`);
                continue;
            }

            try {
                // console.log(`TicketContext: Fetching account details for accountId: ${ticket.accountId}`);
                const accountDetails = await fetchAccountDetails(ticket.accountId);
                // console.log(`TicketContext: Account details for ${ticket.accountId}:`, accountDetails);
                
                const accountName = accountDetails?.accountName || '';
                // console.log(`TicketContext: Account name: "${accountName}"`);
                
                // Get first word of account name in lowercase
                const accountFirstWord = accountName.split(' ')[0].toLowerCase();
                
                // console.log(`TicketContext: Comparing user org "${userOrganizationName}" with account "${accountFirstWord}" for ticket ${ticket.id}`);
                
                // If first word of account name matches user organization, include the ticket
                if (accountFirstWord === userOrganizationName) {
                    // console.log(`TicketContext: ✅ MATCH! Including ticket ${ticket.id}`);
                    filteredTickets.push(ticket);
                } else {
                    // console.log(`TicketContext: ❌ NO MATCH for ticket ${ticket.id}`);
                }
            } catch (accountError) {
                console.warn(`TicketContext: Failed to fetch account details for ticket ${ticket.id}:`, accountError);
                // Continue with other tickets even if one account fetch fails
            }
        }

        // console.log(`TicketContext: Filtered ${filteredTickets.length} tickets out of ${allTickets.length} total tickets`);
        return filteredTickets;
    }, [user, adminAccountFilter]);

    // Fetch all tickets
    const loadTickets = useCallback(async (params = {}) => {
        if (!isAuthenticated) return;
        
        // Wait for user context to be populated before loading tickets
        if (!user || (!user.customerId && !user.tenantId && user.role !== 'helios_admin' && !user.roles?.includes('helios_admin'))) {
            // console.log('TicketContext: Waiting for user context to be populated, user:', user);
            return;
        }

        // console.log('TicketContext: Loading tickets with user context:', user);
        setIsLoading(true);
        setError(null);
        setZohoAuth({ required: false, url: null });

        try {
            const ticketsData = await fetchTickets(params);
            // console.log('TicketContext: Raw tickets data:', ticketsData);
            
            // Apply organization-based filtering
            const filteredTickets = await filterTicketsByOrganization(ticketsData);
            setTickets(filteredTickets);
            setError(null); // Clear any previous gateway error on success
        } catch (err) {
            if (err.requiresZohoAuth && err.authUrl) {
                console.log("Zoho Authentication required. Setting auth state for UI prompt.");
                setZohoAuth({ required: true, url: err.authUrl });
            } else if (err.isZohoGatewayError) {
                // 5xx / gateway error: app continues, show message only
                setError(err.message || "Zoho Desk gateway is temporarily unavailable.");
            } else {
                setError(err.message || "Failed to load tickets.");
            }
            setTickets([]);
        } finally {
            setIsLoading(false);
        }
    }, [isAuthenticated, user, filterTicketsByOrganization]);

    // Fetch a single ticket
    const loadTicket = async (ticketId) => {
        try {
            setIsLoading(true);
            const data = await fetchTicket(ticketId);
            return data;
        } catch (err) {
            // Check for the special error from our API service
            if (err.requiresZohoAuth && err.authUrl) {
                console.log("Zoho Authentication required for ticket fetch. Setting auth state for UI prompt.");
                setZohoAuth({ required: true, url: err.authUrl });
            } else {
                setError(err.message || "Failed to load ticket.");
            }
            console.error('Error loading ticket:', err);
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    // Create a new ticket
    const addTicket = async (ticketData) => {
        try {
            const newTicket = await createTicket(ticketData);
            loadTickets(); // Refresh the list after adding
            return newTicket;
        } catch (err) {
            // Check for the special error from our API service
            if (err.requiresZohoAuth && err.authUrl) {
                console.log("Zoho Authentication required for ticket creation. Setting auth state for UI prompt.");
                setZohoAuth({ required: true, url: err.authUrl });
            } else {
                setError(err.message || "Failed to create ticket.");
            }
            console.error('Error creating ticket:', err);
            throw err;
        }
    };

    // Update an existing ticket
    const editTicket = async (ticketId, updateData) => {
        try {
            setIsLoading(true);
            const updatedTicket = await updateTicket(ticketId, updateData);
            setTickets(prev => prev.map(ticket => 
                ticket.id === ticketId ? updatedTicket : ticket
            ));
            return updatedTicket;
        } catch (err) {
            // Check for the special error from our API service
            if (err.requiresZohoAuth && err.authUrl) {
                console.log("Zoho Authentication required for ticket update. Setting auth state for UI prompt.");
                setZohoAuth({ required: true, url: err.authUrl });
            } else {
                setError(err.message || "Failed to update ticket.");
            }
            console.error('Error updating ticket:', err);
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    loadTicketsRef.current = loadTickets;

    // Load tickets only when auth or stable user identity changes (avoids loop on 5xx)
    useEffect(() => {
        if (!isAuthenticated || !user) {
            setIsLoading(false);
            return;
        }
        loadTicketsRef.current?.();
    }, [isAuthenticated, user?.customerId ?? user?.tenantId ?? user?.email, adminAccountFilter]);

    // Listen for global refresh events
    useEffect(() => {
        const handleGlobalRefresh = () => {
            if (isAuthenticated && user) {
                loadTicketsRef.current?.();
            }
        };

        window.addEventListener('globalRefresh', handleGlobalRefresh);
        return () => window.removeEventListener('globalRefresh', handleGlobalRefresh);
    }, [isAuthenticated, user]);

    const value = {
        tickets,
        isLoading,
        error,
        zohoAuth, // Expose the new state
        loadTickets, // Can be used for manual refresh
        addTicket,
        adminAccountFilter, // Expose admin account filter
        setAdminAccountFilter, // Expose admin account filter setter
    };

    return (
        <TicketContext.Provider value={value}>
            {children}
        </TicketContext.Provider>
    );
};

export const useTickets = () => {
    const context = useContext(TicketContext);
    if (context === undefined) {
        throw new Error('useTickets must be used within a TicketProvider');
    }
    return context;
}; 